import { useEffect } from 'react';
import { getToastMessage } from '@/shared/copy/toasts';
import { queryKeys } from './queryKeys';
import {
  clearProjectStateCaches,
  toProjectOption,
  upsertProjectQueryResult,
  writeCachedOrganisations,
  writeCachedProjectOptions,
  writeProjectStateCaches,
} from './useBootstrapData.cache';
import type { OrganisationOption, ProjectOption } from './useBootstrapData.types';
import type { OrganisationDto, ProjectDto, QueryResult } from '@flowqa/shared';
import type { QueryClient } from '@tanstack/react-query';

type UseBootstrapDataEventsArgs = {
  organisations: OrganisationOption[];
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  userId?: string | null;
  notify: (input: { message: string; tone: 'orange' | 'green' }) => void;
  queryClient: QueryClient;
  setOrganisations: React.Dispatch<React.SetStateAction<OrganisationOption[]>>;
  setProjects: React.Dispatch<React.SetStateAction<ProjectDto[]>>;
  setCachedProjectOptions: React.Dispatch<React.SetStateAction<ProjectOption[]>>;
  setSelectedProjectId: (projectId: string | null) => void;
};

export const useBootstrapDataEvents = ({
  organisations,
  selectedOrganisationId,
  selectedProjectId,
  userId,
  notify,
  queryClient,
  setOrganisations,
  setProjects,
  setCachedProjectOptions,
  setSelectedProjectId,
}: UseBootstrapDataEventsArgs): void => {
  useEffect(() => {
    const handleOrgCreated = (event: Event): void => {
      const detail = (event as CustomEvent<OrganisationDto>).detail;
      if (!detail.id) return;
      setOrganisations((previousOrganisations) => {
        const nextOrganisations = previousOrganisations.some((org) => org.id === detail.id)
          ? previousOrganisations
          : [
              ...previousOrganisations,
              {
                id: detail.id,
                name: detail.name,
                slug: detail.slug,
                clerkOrganizationId: detail.clerkOrganizationId,
              },
            ];
        writeCachedOrganisations(nextOrganisations, userId);
        return nextOrganisations;
      });
    };

    const handleOrgUpdated = (event: Event): void => {
      const detail = (
        event as CustomEvent<{
          id: string;
          name: string;
          clerkOrganizationId?: string | null;
        }>
      ).detail;
      if (!detail.id) return;
      setOrganisations((previousOrganisations) => {
        const nextOrganisations = previousOrganisations.map((org) =>
          org.id === detail.id
            ? {
                ...org,
                name: detail.name,
                clerkOrganizationId: detail.clerkOrganizationId ?? org.clerkOrganizationId,
              }
            : org,
        );
        writeCachedOrganisations(nextOrganisations, userId);
        return nextOrganisations;
      });
    };

    window.addEventListener('flowqa-org-created', handleOrgCreated);
    window.addEventListener('flowqa-org-updated', handleOrgUpdated);
    return () => {
      window.removeEventListener('flowqa-org-created', handleOrgCreated);
      window.removeEventListener('flowqa-org-updated', handleOrgUpdated);
    };
  }, [setOrganisations, userId]);

  useEffect(() => {
    const handleProjectCreated = (event: Event): void => {
      const detail = (event as CustomEvent<ProjectDto>).detail;
      if (!detail.id) return;
      setProjects((previousProjects) =>
        previousProjects.some((project) => project.id === detail.id)
          ? previousProjects
          : [...previousProjects, detail],
      );
      if (detail.organisationId !== selectedOrganisationId) return;
      setCachedProjectOptions((previousOptions) => {
        const nextOptions = previousOptions.some((project) => project.id === detail.id)
          ? previousOptions
          : [...previousOptions, toProjectOption(detail)];
        writeCachedProjectOptions(selectedOrganisationId, nextOptions, userId);
        return nextOptions;
      });
    };

    window.addEventListener('flowqa-project-created', handleProjectCreated);
    return () => {
      window.removeEventListener('flowqa-project-created', handleProjectCreated);
    };
  }, [selectedOrganisationId, setCachedProjectOptions, setProjects, userId]);

  useEffect(() => {
    const handleProjectUpdated = (event: Event): void => {
      const detail = (event as CustomEvent<ProjectDto>).detail;
      if (!detail.id) return;
      setProjects((previousProjects) => {
        const nextProjects = previousProjects.map((project) =>
          project.id === detail.id ? { ...project, ...detail } : project,
        );
        writeProjectStateCaches({
          projects: nextProjects,
          organisations,
          userId,
        });
        return nextProjects;
      });
      queryClient.setQueryData<QueryResult<ProjectDto>>(
        queryKeys.projects(detail.organisationId ?? null),
        (previousResult) => upsertProjectQueryResult(previousResult, detail),
      );
      if (detail.organisationId !== selectedOrganisationId) return;
      setCachedProjectOptions((previousOptions) => {
        const nextOptions = previousOptions.map((project) =>
          project.id === detail.id ? toProjectOption(detail) : project,
        );
        writeCachedProjectOptions(selectedOrganisationId, nextOptions, userId);
        return nextOptions;
      });
    };

    window.addEventListener('flowqa-project-updated', handleProjectUpdated);
    return () => {
      window.removeEventListener('flowqa-project-updated', handleProjectUpdated);
    };
  }, [
    organisations,
    queryClient,
    selectedOrganisationId,
    setCachedProjectOptions,
    setProjects,
    userId,
  ]);

  useEffect(() => {
    const handleProjectRemoved = (event: Event): void => {
      const detail = (event as CustomEvent<{ id: string }>).detail;
      if (!detail.id) return;
      setProjects((previousProjects) => {
        const nextProjects = previousProjects.filter((project) => project.id !== detail.id);
        writeProjectStateCaches({
          projects: nextProjects,
          organisations,
          userId,
        });
        return nextProjects;
      });
      setCachedProjectOptions((previousOptions) => {
        const nextOptions = previousOptions.filter((project) => project.id !== detail.id);
        writeCachedProjectOptions(selectedOrganisationId, nextOptions, userId);
        return nextOptions;
      });
      if (selectedProjectId === detail.id) {
        setSelectedProjectId(null);
      }
    };

    window.addEventListener('flowqa-project-removed', handleProjectRemoved);
    return () => {
      window.removeEventListener('flowqa-project-removed', handleProjectRemoved);
    };
  }, [
    organisations,
    selectedOrganisationId,
    selectedProjectId,
    setCachedProjectOptions,
    setProjects,
    setSelectedProjectId,
    userId,
  ]);

  useEffect(() => {
    const handleOrgRemoved = (event: Event): void => {
      const detail = (event as CustomEvent<{ reason?: string } | undefined>).detail;
      clearProjectStateCaches(userId);
      queryClient.removeQueries({ queryKey: queryKeys.organisationsPrefix() });
      queryClient.removeQueries({ queryKey: queryKeys.projects(null) });
      if (selectedOrganisationId !== null) {
        queryClient.removeQueries({ queryKey: queryKeys.projects(selectedOrganisationId) });
      }
      setOrganisations([]);
      setProjects([]);
      setCachedProjectOptions([]);
      setSelectedProjectId(null);
      if (detail?.reason === 'deleted_by_user') {
        return;
      }
      notify({ message: getToastMessage('workspace.accessLost'), tone: 'orange' });
    };

    window.addEventListener('flowqa-org-removed', handleOrgRemoved);
    return () => {
      window.removeEventListener('flowqa-org-removed', handleOrgRemoved);
    };
  }, [
    notify,
    queryClient,
    selectedOrganisationId,
    setCachedProjectOptions,
    setOrganisations,
    setProjects,
    setSelectedProjectId,
    userId,
  ]);
};
